import type { ResultsRuntimeSnapshot } from "../application/results-runtime";
import { getModeConfig } from "../config/gameplay-config";
import { renderSnakeVisual } from "./snake-visual";

export interface ResultsShellRuntime {
  playAgain(): boolean;
  retrySettlement(): boolean;
  showHome(): boolean;
  subscribe(listener: (snapshot: ResultsRuntimeSnapshot) => void): () => void;
}

export type ResultsShellTimer = ReturnType<typeof globalThis.setTimeout>;

export interface ResultsShellScheduler {
  clear(timer: ResultsShellTimer): void;
  schedule(callback: () => void, delayMs: number): ResultsShellTimer;
}

export interface ResultsShellElements {
  readonly bestState: HTMLElement;
  readonly coinsEarned: HTMLElement;
  readonly highlightOne: HTMLElement;
  readonly highlightTwo: HTMLElement;
  readonly homeButton: HTMLButtonElement;
  readonly mode: HTMLElement;
  readonly outcome: HTMLElement;
  readonly personalBest: HTMLElement;
  readonly playAgainButton: HTMLButtonElement;
  readonly retryButton: HTMLButtonElement;
  readonly screen: HTMLElement;
  readonly score: HTMLElement;
  readonly settlement: HTMLElement;
  readonly snake: HTMLElement;
  readonly totalCoins: HTMLElement;
}

interface ResultHighlight {
  readonly icon: string;
  readonly label: string;
}

const celebrationDurationMs = 1_100;

const browserScheduler: ResultsShellScheduler = {
  clear: (timer) => {
    globalThis.clearTimeout(timer);
  },
  schedule: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
};

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing results shell element: ${id}`);
  }
  return element;
};

export class ResultsShell {
  private celebratedResultId: string | null = null;
  private celebrationTimer: ResultsShellTimer | null = null;
  private readonly unsubscribe: () => void;

  public constructor(
    private readonly runtime: ResultsShellRuntime,
    private readonly elements: ResultsShellElements,
    private readonly scheduler: ResultsShellScheduler = browserScheduler,
  ) {
    elements.homeButton.addEventListener("click", this.handleHome);
    elements.playAgainButton.addEventListener("click", this.handlePlayAgain);
    elements.retryButton.addEventListener("click", this.handleRetry);
    this.unsubscribe = runtime.subscribe((snapshot) => {
      this.render(snapshot);
    });
  }

  public destroy(): void {
    this.clearCelebration();
    this.elements.homeButton.removeEventListener("click", this.handleHome);
    this.elements.playAgainButton.removeEventListener(
      "click",
      this.handlePlayAgain,
    );
    this.elements.retryButton.removeEventListener("click", this.handleRetry);
    this.unsubscribe();
  }

  private readonly handleHome = (): void => {
    this.runtime.showHome();
  };

  private readonly handlePlayAgain = (): void => {
    this.runtime.playAgain();
  };

  private readonly handleRetry = (): void => {
    this.runtime.retrySettlement();
  };

  private render(snapshot: ResultsRuntimeSnapshot): void {
    const result = snapshot.result;
    this.elements.screen.hidden = result === null;
    if (result === null) {
      this.celebratedResultId = null;
      this.clearCelebration();
      return;
    }
    this.elements.outcome.textContent = result.outcome;
    this.elements.mode.textContent =
      result.challenge === undefined
        ? getModeConfig(result.mode).displayName
        : result.challenge.resultsModeLabel;
    if (result.challenge === undefined) {
      delete this.elements.screen.dataset.challengeIdentity;
    } else {
      this.elements.screen.dataset.challengeIdentity =
        result.challenge.identity;
    }
    renderSnakeVisual(
      this.elements.snake,
      result.selectedSkinId,
      result.finalLevel,
      `Your Level ${String(result.finalLevel)} snake`,
    );
    this.elements.coinsEarned.textContent = `+${String(result.coinsEarned)}`;
    this.elements.totalCoins.textContent = String(result.totalCoins);
    this.elements.score.textContent = `Level ${String(result.finalLevel)}`;
    this.elements.personalBest.textContent = `Level ${String(result.personalBestLevel)}`;
    this.elements.bestState.textContent = result.isNewBestLevel
      ? "New best"
      : "Personal best";
    this.elements.personalBest.dataset.newBest = String(result.isNewBestLevel);
    const highlights: ResultHighlight[] = [];
    if (result.challenge !== undefined) {
      highlights.push({
        icon: "✓",
        label: result.challenge.resultHighlight,
      });
    }
    if (result.snakesDefeated > 0) {
      highlights.push({
        icon: "🐍",
        label: `${String(result.snakesDefeated)} snake${result.snakesDefeated === 1 ? "" : "s"} beaten`,
      });
    }
    this.renderHighlight(this.elements.highlightOne, highlights[0]);
    this.renderHighlight(this.elements.highlightTwo, highlights[1]);
    this.startCelebration(result);
    this.elements.homeButton.disabled = !snapshot.navigationAvailable;
    this.elements.playAgainButton.disabled = !snapshot.navigationAvailable;
    const retryVisible =
      snapshot.settlementStatus === "failed" ||
      snapshot.settlementStatus === "conflict";
    this.elements.retryButton.hidden = !retryVisible;
    this.elements.settlement.hidden = !retryVisible;
    this.elements.settlement.textContent = retryVisible
      ? "Progress did not save. Try again."
      : "";
  }

  private clearCelebration(): void {
    if (this.celebrationTimer !== null) {
      this.scheduler.clear(this.celebrationTimer);
      this.celebrationTimer = null;
    }
    delete this.elements.screen.dataset.celebration;
    delete this.elements.screen.dataset.earnedCoins;
    delete this.elements.screen.dataset.newBest;
  }

  private renderHighlight(
    element: HTMLElement,
    highlight: ResultHighlight | undefined,
  ): void {
    element.hidden = highlight === undefined;
    element.textContent =
      highlight === undefined ? "" : `${highlight.icon} ${highlight.label}`;
  }

  private startCelebration(
    result: NonNullable<ResultsRuntimeSnapshot["result"]>,
  ): void {
    if (result.id === this.celebratedResultId) {
      return;
    }
    this.clearCelebration();
    this.celebratedResultId = result.id;
    this.elements.screen.dataset.celebration = "timer-complete";
    this.elements.screen.dataset.earnedCoins = String(result.coinsEarned > 0);
    this.elements.screen.dataset.newBest = String(result.isNewBestLevel);
    this.celebrationTimer = this.scheduler.schedule(() => {
      this.clearCelebration();
    }, celebrationDurationMs);
  }
}

export const installResultsShell = (
  documentScope: Document,
  runtime: ResultsShellRuntime,
): ResultsShell =>
  new ResultsShell(runtime, {
    bestState: requireElement(documentScope, "results-best-state"),
    coinsEarned: requireElement(documentScope, "results-coins-earned"),
    highlightOne: requireElement(documentScope, "results-highlight-one"),
    highlightTwo: requireElement(documentScope, "results-highlight-two"),
    homeButton: requireElement(
      documentScope,
      "results-home-button",
    ) as HTMLButtonElement,
    mode: requireElement(documentScope, "results-mode"),
    outcome: requireElement(documentScope, "results-outcome"),
    personalBest: requireElement(documentScope, "results-personal-best"),
    playAgainButton: requireElement(
      documentScope,
      "results-play-again-button",
    ) as HTMLButtonElement,
    retryButton: requireElement(
      documentScope,
      "results-retry-button",
    ) as HTMLButtonElement,
    screen: requireElement(documentScope, "results-screen"),
    score: requireElement(documentScope, "results-score"),
    settlement: requireElement(documentScope, "results-settlement"),
    snake: requireElement(documentScope, "results-snake"),
    totalCoins: requireElement(documentScope, "results-total-coins"),
  });
